import isEmail from 'validator/lib/isEmail';
import tlds from 'tlds/index.json';

const VALID_TLDS = new Set((tlds as string[]).map((t) => t.toLowerCase()));

/**
 * Email validation matching backend Joi `.email()` behavior.
 * Validates format via the `validator` library and checks the TLD
 * against the IANA-registered TLD list (same list Joi uses via @sideway/address).
 */
export function isValidEmail(email: string): boolean {
  if (!isEmail(email, { require_tld: true, allow_utf8_local_part: false })) return false;
  const tld = (email.split('.').pop() || '').toLowerCase();
  return VALID_TLDS.has(tld);
}

/**
 * DEA Number Validation
 * 
 * DEA numbers follow this format:
 * - 9 characters total
 * - First 2 characters are letters
 * - Last 7 characters are digits
 * - The 9th digit is a check digit calculated using the DEA algorithm
 */

/**
 * Validates DEA number format and checksum
 * @param deaNumber - The DEA number to validate
 * @returns true if valid, false otherwise
 */
export function isValidDEANumber(deaNumber: string): boolean {
  // Remove any whitespace
  const cleanedDEA = deaNumber.trim();
  
  // Check if exactly 9 characters
  if (cleanedDEA.length !== 9) {
    return false;
  }
  
  // Check format: 2 letters followed by 7 digits
  const formatRegex = /^[A-Za-z]{2}\d{7}$/;
  if (!formatRegex.test(cleanedDEA)) {
    return false;
  }
  
  // Extract the digits for checksum calculation
  const digits = cleanedDEA.substring(2);
  
  // Calculate checksum
  // Step 1: Add the 1st, 3rd, and 5th digits (index 0, 2, 4)
  const sum1 = parseInt(digits[0]) + parseInt(digits[2]) + parseInt(digits[4]);
  
  // Step 2: Add the 2nd, 4th, and 6th digits (index 1, 3, 5), then multiply by 2
  const sum2 = (parseInt(digits[1]) + parseInt(digits[3]) + parseInt(digits[5])) * 2;
  
  // Step 3: Add the results from steps 1 and 2
  const totalSum = sum1 + sum2;
  
  // Step 4: The check digit is the rightmost digit of this sum
  const calculatedCheckDigit = totalSum % 10;
  
  // Step 5: Compare with the actual check digit (7th digit, index 6)
  const actualCheckDigit = parseInt(digits[6]);
  
  return calculatedCheckDigit === actualCheckDigit;
}

/**
 * Get a descriptive error message for invalid DEA numbers
 * @param deaNumber - The DEA number to validate
 * @returns Error message or null if valid
 */
export function getDEAValidationError(deaNumber: string): string | null {
  const cleanedDEA = deaNumber.trim();
  
  if (cleanedDEA.length === 0) {
    return null; // Empty is allowed (optional field)
  }
  
  if (cleanedDEA.length !== 9) {
    return 'DEA number must be exactly 9 characters';
  }
  
  const formatRegex = /^[A-Za-z]{2}\d{7}$/;
  if (!formatRegex.test(cleanedDEA)) {
    return 'DEA number must be 2 letters followed by 7 digits (e.g., AB1234563)';
  }
  
  if (!isValidDEANumber(cleanedDEA)) {
    return 'Invalid DEA number checksum. Please verify the DEA number is correct';
  }
  
  return null;
} 